import { useEffect, useState } from "react";
import { ChevronDown, FileText, X } from "lucide-react";
import { EditorSelection } from "@codemirror/state";
import { useTabs } from "@/store/tabs";
import { getData, resolveItem, useItem, useSpaceOf, useWorkspaceItems } from "@/data/store";
import { useFolderMeta } from "@/data/sharedFolders";
import { searchService } from "@/data/search/client";
import { getEditor } from "@/editor/registry";
import { openItem } from "@/commands/nav";
import type { NoteHandle } from "@/data/notes";
import s from "./panels.module.css";
import r from "./DetailsPanel.module.css";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={s.section}>
      <button
        type="button"
        className={[s.sectionHeader, open ? "" : s.sectionCollapsed].join(" ")}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <ChevronDown size={12} />
        {title}
      </button>
      {open ? <div className={s.sectionBody}>{children}</div> : null}
    </div>
  );
}

export function DetailsPanel() {
  const activeItemId = useTabs((s) => {
    const g = s.groups.find((g) => g.id === s.activeGroupId);
    const t = g?.tabs.find((t) => t.id === g.activeTabId);
    return t?.itemId ?? null;
  });
  const item = useItem(activeItemId);
  const space = useSpaceOf(item?.id ?? null);
  const isNote = item?.type === "note";

  return (
    <>
      <div className={s.body}>
        {!item ? (
          <div className={s.empty}>Open a note to see its outline, backlinks, and info.</div>
        ) : (
          <>
            {isNote ? <OutlineSection noteId={item.id} /> : null}
            {space?.kind === "shared" ? (
              <Section title="Backlinks">
                <div className={s.empty}>Backlinks are not available in shared folders yet.</div>
              </Section>
            ) : (
              <BacklinksSection title={item.title} />
            )}
            <InfoSection itemId={item.id} />
          </>
        )}
      </div>
    </>
  );
}

interface Heading {
  level: number;
  text: string;
  line: number;
}

function OutlineSection({ noteId }: { noteId: string }) {
  const [headings, setHeadings] = useState<Heading[]>([]);

  useEffect(() => {
    let handle: NoteHandle | null = (resolveItem(noteId)?.ctx ?? getData()).notes.open(noteId);
    let timer: ReturnType<typeof setTimeout> | null = null;

    const parse = () => {
      if (!handle) return;
      const out: Heading[] = [];
      const lines = handle.text.toString().split("\n");
      let inFence = false;
      lines.forEach((lineText, i) => {
        if (/^\s*(```|~~~)/.test(lineText)) inFence = !inFence;
        if (inFence) return;
        const m = /^(#{1,6})\s+(.+)/.exec(lineText);
        if (m) out.push({ level: m[1]!.length, text: m[2]!.trim(), line: i + 1 });
      });
      setHeadings(out);
    };

    const onUpdate = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(parse, 300);
    };
    void handle.whenLoaded.then(parse);
    handle.doc.on("update", onUpdate);

    return () => {
      if (timer) clearTimeout(timer);
      handle?.doc.off("update", onUpdate);
      handle?.release();
      handle = null;
    };
  }, [noteId]);

  return (
    <Section title="Outline">
      {headings.length === 0 ? (
        <div className={s.empty}>No headings yet.</div>
      ) : (
        headings.map((h, i) => (
          <button
            key={i}
            type="button"
            className={r.outlineItem}
            style={{ paddingLeft: 6 + (h.level - 1) * 12 }}
            onClick={() => {
              const view = getEditor(noteId);
              if (!view) return;
              const line = view.state.doc.line(Math.min(h.line, view.state.doc.lines));
              view.dispatch({
                selection: EditorSelection.cursor(line.from),
                effects: [],
                scrollIntoView: true,
              });
              view.focus();
            }}
          >
            {h.text}
          </button>
        ))
      )}
    </Section>
  );
}

function BacklinksSection({ title }: { title: string }) {
  const items = useWorkspaceItems();
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    void searchService.backlinksTo(title).then((found) => {
      if (alive) setIds(found);
    });
    return () => {
      alive = false;
    };
  }, [title, items]);

  const linked = ids
    .map((id) => items.find((i) => i.id === id))
    .filter((x): x is NonNullable<typeof x> => !!x && !x.trashedAt);

  return (
    <Section title={`Backlinks${linked.length ? ` (${linked.length})` : ""}`}>
      {linked.length === 0 ? (
        <div className={s.empty}>No notes link here yet.</div>
      ) : (
        linked.map((i) => (
          <button key={i.id} type="button" className={r.backlink} onClick={() => openItem(i.id)}>
            <FileText size={12} />
            <span>{i.title || "Untitled"}</span>
          </button>
        ))
      )}
    </Section>
  );
}

/** Workspace store of whichever space an item lives in. */
function wsOf(itemId: string) {
  return (resolveItem(itemId)?.ctx ?? getData()).workspace;
}

function InfoSection({ itemId }: { itemId: string }) {
  const item = useItem(itemId);
  const space = useSpaceOf(itemId);
  const folderMeta = useFolderMeta(space?.kind === "shared" ? space.spaceId : null);
  const readOnly = space?.kind === "shared" && (folderMeta?.role === "viewer" || !!folderMeta?.locked);
  const [wordCount, setWordCount] = useState<number | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  const [aliasDraft, setAliasDraft] = useState("");

  useEffect(() => {
    if (!item || item.type !== "note") {
      setWordCount(null);
      return;
    }
    let alive = true;
    void (resolveItem(itemId)?.ctx ?? getData())
      .notes.readMarkdown(itemId)
      .then((md) => {
        if (alive) setWordCount(md.trim() ? md.trim().split(/\s+/).length : 0);
      });
    return () => {
      alive = false;
    };
  }, [itemId, item?.updatedAt, item?.type]);

  if (!item) return null;

  const addTag = () => {
    const t = tagDraft.trim().replace(/^#/, "").toLowerCase();
    if (t && !item.tags.includes(t)) {
      wsOf(item.id).setTags(item.id, [...item.tags, t]);
    }
    setTagDraft("");
  };

  return (
    <Section title="Info">
      <div className={r.infoGrid}>
        <span className={r.infoLabel}>Created</span>
        <span className={r.infoValue}>{new Date(item.createdAt).toLocaleString()}</span>
        <span className={r.infoLabel}>Edited</span>
        <span className={r.infoValue}>{new Date(item.updatedAt).toLocaleString()}</span>
        {wordCount !== null ? (
          <>
            <span className={r.infoLabel}>Words</span>
            <span className={r.infoValue}>{wordCount}</span>
          </>
        ) : null}
      </div>
      <div className={r.tags}>
        {item.tags.map((t) => (
          <span key={t} className={r.tag}>
            #{t}
            <button
              type="button"
              className={r.tagRemove}
              aria-label={`Remove tag ${t}`}
              onClick={() =>
                wsOf(item.id).setTags(
                  item.id,
                  item.tags.filter((x) => x !== t),
                )
              }
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {readOnly ? null : (
        <input
          className={r.tagInput}
          placeholder="+ tag"
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addTag();
            if (e.key === "Escape") setTagDraft("");
          }}
          onBlur={() => tagDraft && addTag()}
        />
        )}
      </div>
      {item.type === "note" ? (
        <div className={r.tags}>
          {(item.aliases ?? []).map((a) => (
            <span key={a} className={[r.tag, r.alias].join(" ")}>
              {a}
              <button
                type="button"
                className={r.tagRemove}
                aria-label={`Remove alias ${a}`}
                onClick={() =>
                  wsOf(item.id).setAliases(
                    item.id,
                    (item.aliases ?? []).filter((x) => x !== a),
                  )
                }
              >
                <X size={10} />
              </button>
            </span>
          ))}
          {readOnly ? null : (
          <input
            className={r.tagInput}
            placeholder="+ alias"
            title="Alternate titles the switcher and [[wikilinks]] will match"
            value={aliasDraft}
            onChange={(e) => setAliasDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addAlias();
              if (e.key === "Escape") setAliasDraft("");
            }}
            onBlur={() => aliasDraft && addAlias()}
          />
          )}
        </div>
      ) : null}
    </Section>
  );

  function addAlias() {
    if (!item) return;
    const a = aliasDraft.trim();
    if (a && !(item.aliases ?? []).includes(a)) {
      wsOf(item.id).setAliases(item.id, [...(item.aliases ?? []), a]);
    }
    setAliasDraft("");
  }
}
